import { memo, useCallback, useMemo, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { assetsClipUpdate } from '@goldcast/api/content';
import { getIsDuplicateRecording, isNotFullLengthClip } from '@goldcast/player-config/src/utils/clipUtils';
import TooltipV2 from '../TooltipV2';
import ClipPublishStatusBadge from './ClipPublish/ClipPublishStatusBadge';
import ClipPublishButton from './ClipPublish/ClipPublishButton';
import ClipUpdateInProgress from './ClipUpdateInProgress/ClipUpdateInProgress';
import ClipTitlePopup from '../atoms/ClipTitlePopup';
import IconButton from '../atoms/Button/IconButton';
import Icon from '../atoms/Icon';
import FreeTrialUpgradeNag from '@/components/molecules/FreeTrial/FreeTrialUpgradeNag';
import ClipUndoRedo from '@/components/molecules/ClipUndoRedo';
import DownloadsPopup from '@/components/molecules/DownloadsPopup/DownloadsPopup';
import ClipSharePopup from '@/components/molecules/SharePopup/ClipSharePopup/ClipSharePopup';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useSavedSearch from '@/hooks/useSavedSearch';
import useSmallScreen from '@/hooks/useSmallScreen';
import useFreeTrialHook from '@/hooks/useFreeTrialHook';
import ContentSettings from '@/components/molecules/ContentSettings';
import SavedSearchTitlePopup from '@/Pages/SavedSearch/SavedSearchTitlePopup';
import SessionTitlePopup from '@/Pages/SessionDetail/SessionTitlePopup';
import NewTemplateButton from '@/Pages/Clip/SideBar/TemplatesSideBar/NewTemplateButton';
import RecordingDuplicateDropdown from '@/Pages/RecordingsPage/RecordingDuplicateDropdown';
import { core } from '@/stores/core';
import { useAppContext } from '@/context/AppContext/AppContext';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { handleClipLockedError } from '@/Pages/Clip/utils';
import { classnames } from '@/libs/utils';
import { aiEditsStore } from '@/stores/aiEditsStore';
import { orgUsageSummaryState } from '@/stores/orgUsageSummary';

function ClipHeader({ showLock, newPlayer }: { showLock?: boolean; newPlayer?: boolean }) {
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const { adminAppStore } = useAppContext();
  const { clipData } = useClipsContext();

  const location = useLocation();
  const { isSavedSearchPage } = useSavedSearch();

  // Responsive breakpoint detection
  const isSmallScreen = useSmallScreen();

  const { isAIEditsSidebarOpen } = useSyncExternalStore(aiEditsStore.subscribe, aiEditsStore.getSnapshot);

  const orgUsageSummaryStore = useSyncExternalStore(orgUsageSummaryState.subscribe, orgUsageSummaryState.getSnapshot);
  const { isFreeTrialEnabled, isFreemiumUser } = useFreeTrialHook();
  const isFreeTrialButtonEnabled = useMemo(() => {
    const showUpgradeButton = isFreeTrialEnabled || isFreemiumUser;
    const isStoreReady = !orgUsageSummaryStore.loading && !orgUsageSummaryStore.error && orgUsageSummaryStore.orgId;
    return showUpgradeButton && isStoreReady;
  }, [
    isFreeTrialEnabled,
    isFreemiumUser,
    orgUsageSummaryStore.loading,
    orgUsageSummaryStore.error,
    orgUsageSummaryStore.orgId
  ]);

  const isRecordingsPage = useMemo(() => {
    return location.pathname.includes('/recordings');
  }, [location.pathname]);

  const isClipListPage = useMemo(() => {
    return location.pathname.includes('/clips');
  }, [location.pathname]);

  const isVideoLibraryClipPublishEnabled = useMemo(
    () => adminAppStore.getters?.['org/getCurrentOrg']?.showVideoLibraryClipPublish || false,
    [adminAppStore]
  );

  const updateRecordingTitle = useCallback(() => {
    const title = core.getSnapshot().content?.title;
    if (isRecordingsPage && title) {
      assetsClipUpdate({
        id: clipData.id,
        body: {
          ...clipData,
          title,
          asset_metadata: clipData.asset_metadata
        }
      });
    }
  }, [clipData, isRecordingsPage]);

  const isPublishingAllowed = useMemo(() => {
    // For clip page
    if (isVideoLibraryClipPublishEnabled && isClipListPage) {
      return true;
    }

    // For recording studio recordings
    if (coreStore.content?.event_type === 'Recording' && isRecordingsPage) {
      return true;
    }

    // For event recordings
    if (coreStore.content?.media_source_type === 'RECORDING' && isRecordingsPage) {
      return true;
    }

    // For content lab uploads
    if (featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Uploads_In_VL] && isRecordingsPage) {
      return true;
    }

    return false;
  }, [
    coreStore.content?.event_type,
    coreStore.content?.media_source_type,
    isVideoLibraryClipPublishEnabled,
    isRecordingsPage,
    isClipListPage
  ]);

  const shouldShowClipTitle = useMemo(() => {
    if (isNotFullLengthClip(clipData) || getIsDuplicateRecording(clipData)) {
      return true;
    }
    return false;
  }, [clipData]);

  const shouldShowTemplateAsIcon = useMemo(() => {
    return Boolean(isSmallScreen || isFreeTrialButtonEnabled);
  }, [isSmallScreen, isFreeTrialButtonEnabled]);

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isDownloadRevampEnabled = !!featureFlags[FeatureFlagKeys.Use_CL_Download_Revamp];
  const isClipDuplicationEnabled = !!featureFlags[FeatureFlagKeys.Use_CL_Clip_Duplication];

  return (
    <div
      className={classnames(
        'relative flex items-center justify-between p-4',
        newPlayer ? 'w-[calc(100%+var(--clip-customizer-menu-width))]' : ''
      )}
    >
      <div className="flex items-center space-x-1">
        <ClipUpdateInProgress clipId={clipData.id} />
        <div
          className={classnames('flex items-center', isSmallScreen ? 'max-w-48' : 'max-w-60', {
            'max-2xl:hidden': isAIEditsSidebarOpen
          })}
        >
          {isSavedSearchPage ? (
            <>
              <SavedSearchTitlePopup />
              {clipData?.saved_search?.search_term && (
                <TooltipV2
                  tooltipId="edit-saved-search-title-tooltip"
                  tooltipElement={<Icon name="IconInfoCircle" size={16} className="ml-1 text-slate-950" />}
                  tooltipContent={clipData?.saved_search?.search_term}
                  size="xsmall"
                  colorTheme="dark"
                  place="right"
                  className="z-50 !rounded-md bg-slate-950 px-3 py-2 !text-[10px] font-normal !leading-[15px] text-white"
                />
              )}
            </>
          ) : (
            <>
              {shouldShowClipTitle ? <ClipTitlePopup /> : <SessionTitlePopup onUpdate={updateRecordingTitle} />}
              {isRecordingsPage && isClipDuplicationEnabled && <RecordingDuplicateDropdown />}
            </>
          )}
        </div>
        {isPublishingAllowed && (
          <div className={classnames({ 'max-2xl:hidden': isAIEditsSidebarOpen })}>
            <ClipPublishStatusBadge />
          </div>
        )}
        {showLock && (
          <IconButton
            icon="IconLock"
            title={`Clip is published in ${clipData.content.project.title}'s on-demand page and cannot be edited. Please un-publish to continue editing.`}
            onClick={() => handleClipLockedError(clipData)}
            trackingId="clips-list-item-locked-button"
            variation="outline"
            iconClassName="h-4 w-4 text-slate-600"
            buttonClassName="h-8 w-8"
          />
        )}
      </div>
      <div className="flex items-center space-x-3">
        <FreeTrialUpgradeNag />
        <ClipUndoRedo isInline={true} />
        {isDownloadRevampEnabled ? (
          <>
            <NewTemplateButton
              className={classnames('bg-white', shouldShowTemplateAsIcon ? 'w-[38px]' : '')}
              isIconOnly={shouldShowTemplateAsIcon}
            />
            <ClipSharePopup
              iconButtonVariation={isPublishingAllowed ? 'outline' : 'filled'}
              activeClipId={clipData.id}
              downloadIntent={location.state?.downloadIntent}
            />
            {coreStore.content?.id && <DownloadsPopup contentId={coreStore.content.id} />}
            <ClipPublishButton isPublishingAllowed={isPublishingAllowed} />
          </>
        ) : (
          <>
            {coreStore.content?.id && <DownloadsPopup contentId={coreStore.content.id} />}
            <NewTemplateButton
              className={classnames('bg-white', shouldShowTemplateAsIcon ? 'w-[38px]' : '')}
              isIconOnly={shouldShowTemplateAsIcon}
            />
            <ClipPublishButton isPublishingAllowed={isPublishingAllowed} />
            <ClipSharePopup
              iconButtonVariation={isPublishingAllowed ? 'outline' : 'filled'}
              activeClipId={clipData.id}
              downloadIntent={location.state?.downloadIntent}
            />
          </>
        )}
        {isSavedSearchPage && <ContentSettings />}
      </div>
    </div>
  );
}

export default memo(ClipHeader);
